import type { MutableRefObject } from 'react';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import type { CadFeatureMap } from '@mark43/cad-api';
import type { MapView } from '../constructors';
import type { MapSurface } from '../types';
import { MAIN_MAP_SURFACE } from '../types';
import { logMapError, setupWebGLContextListeners } from './utils';
import type { MapSource } from './utils';
import {
    getMapErrorCounts,
    incrementFatalErrorCount,
    incrementViewResumeCount,
    incrementViewSuspendCount,
} from './map-diagnostic-context';
import { createMapRecoveryTracker } from './map-recovery-tracker';
import { pickMapRelevantFlags } from './map-relevant-flags';
import { getWebGLUnavailableReason } from './webgl-unavailable-reason';

/**
 * Everything the app records about a MapView's health, attached to one view: WebGL
 * context loss, fatal SDK errors, the recovery attempt, and the episode tracker that
 * turns those into one outcome per failure.
 *
 * `useOwnedMapView` attaches this to every view it creates, so a map can no more opt out
 * of health monitoring than it can opt out of teardown — and the ESLint rule that funnels
 * every `new MapView` through `useOwnedMapView` enforces both. This used to be ~170 lines
 * duplicated across the two map hooks, and the copies had quietly drifted apart.
 */

type MapContainerRef = MutableRefObject<HTMLDivElement | null>;

/** App state the monitor needs, supplied by `useMapHealthInputs`. */
export type MapHealthInputs = {
    /** Flags as of the moment the view became ready. Stamped on episode events. */
    enabledFeatures: CadFeatureMap;
    /** Read when an event fires, because the flag can change mid-session. */
    isDiagnosticContextEnabled: () => boolean;
    /** The full diagnostic context: map, renderer, and Electron main (over IPC). */
    collectDiagnosticContext: (
        view: MapView,
        mapRef: MapContainerRef,
        isEmbeddedMap: boolean
    ) => Promise<unknown>;
};

export type MapHealthMonitorOptions = MapHealthInputs & {
    view: MapView;
    surface: MapSurface;
    /** When the view was created. Episodes measure the map's age from here. */
    createdAtMs: number;
    /** The view's container. Its canvases are the ones watched for context loss. */
    mapRef: MapContainerRef;
    /** Count Esri's suspend/resume cycles. Only the main map ever has. */
    countSuspendCycles: boolean;
};

/**
 * Attach health monitoring to a view. Returns a disposer that detaches all of it,
 * including the Esri watchers, which used to be left registered on destroyed views.
 *
 * Never throws: monitoring failing must not fail the map. It used to run inside the
 * hooks' `view.when()` callback, where an exception reached their `.catch` and showed
 * the user a "map failed to load" state.
 */
export function monitorMapHealth({
    view,
    surface,
    createdAtMs,
    mapRef,
    enabledFeatures,
    isDiagnosticContextEnabled,
    collectDiagnosticContext,
    countSuspendCycles,
}: MapHealthMonitorOptions): () => void {
    const disposers: Array<() => void> = [];
    const isEmbeddedMap = surface !== MAIN_MAP_SURFACE;

    try {
        const tracker = createMapRecoveryTracker({
            surface,
            enabledFeatures: pickMapRelevantFlags(enabledFeatures),
            createdAtMs,
            getFatalErrorName: () => view.fatalError?.name,
            getViewSuspended: () => (view as unknown as { suspended?: boolean }).suspended,
            getContainer: () => mapRef.current,
        });
        disposers.push(() => tracker.dispose());

        const logWithDiagnosticContext = (
            source: MapSource,
            event: string,
            extraData: Record<string, unknown>
        ) => {
            const log = (diagnosticContext?: unknown) =>
                logMapError({
                    source,
                    event,
                    surface,
                    extraData:
                        diagnosticContext === undefined
                            ? { ...extraData, hasDiagnosticContext: false }
                            : { ...extraData, hasDiagnosticContext: true, diagnosticContext },
                });

            if (!isDiagnosticContextEnabled()) {
                log();
                return;
            }
            // Collection crosses IPC to Electron main, so it is async. Kicked off rather
            // than returned: Esri's `reactiveUtils.when` and `view.when` consume their
            // callbacks' return values, and a returned promise is swallowed.
            void (async () => {
                try {
                    log(await collectDiagnosticContext(view, mapRef, isEmbeddedMap));
                } catch {
                    log();
                }
            })();
        };

        disposers.push(
            setupWebGLContextListeners(
                mapRef,
                surface,
                (event, canvas) =>
                    logWithDiagnosticContext('webgl', 'context_lost', {
                        statusMessage: event.statusMessage,
                        canvasWidth: canvas.width,
                        canvasHeight: canvas.height,
                    }),
                () => tracker.onRecoverySignal('context_restored')
            )
        );

        // One recovery attempt per view, whatever the first fatal error was. The SDK
        // re-emits fatalError after a failed tryFatalErrorRecovery(), and every attempt
        // allocates another WebGL context, so the limit is what stops a failing view from
        // looping. Within it, an attempt is never worse than none.
        let hasAttemptedRecovery = false;
        const fatalErrorHandle = reactiveUtils.when(
            () => view.fatalError,
            (fatalError) => {
                if (hasAttemptedRecovery) {
                    return;
                }
                hasAttemptedRecovery = true;

                incrementFatalErrorCount();
                logWithDiagnosticContext('sdk', 'fatal_error', {
                    errorName: fatalError?.name,
                    errorMessage: fatalError?.message,
                });

                if (fatalError?.name === 'webgl-context-lost') {
                    tracker.onFailure();
                }
                view.tryFatalErrorRecovery();
            }
        );
        disposers.push(() => fatalErrorHandle.remove());

        // fatalError clearing is logged as a recovery signal but not trusted to close an
        // episode: it also happens mid-attempt while the map is still black.
        const fatalErrorClearedHandle = reactiveUtils.watch(
            () => view.fatalError,
            (fatalError, previousFatalError) => {
                if (previousFatalError && !fatalError) {
                    tracker.onRecoverySignal('fatal_error_cleared');
                }
            }
        );
        disposers.push(() => fatalErrorClearedHandle.remove());

        if (countSuspendCycles) {
            // Esri suspends a view whose container has ~zero area, which is what happens
            // to the hidden First Responder map.
            const suspendHandle = reactiveUtils.watch(
                () => (view as unknown as { suspended?: boolean }).suspended === true,
                (suspended: boolean) => {
                    if (suspended) {
                        incrementViewSuspendCount();
                    } else {
                        incrementViewResumeCount();
                    }
                }
            );
            disposers.push(() => suspendHandle.remove());
        }
    } catch {
        // Whatever was attached before the failure is still detached by the disposer.
    }

    return function stopMonitoringMapHealth() {
        for (const dispose of disposers) {
            try {
                dispose();
            } catch {
                // One step failing must not leave the rest attached.
            }
        }
    };
}

/** Log a view that never became ready. */
export function reportMapInitFailure(
    error: unknown,
    surface: MapSurface,
    createdAtMs: number
): void {
    // Passed through as-is: Esri's errors are not `Error` instances, and Datadog
    // already serializes them usefully.
    const sdkError = error as Error;
    const extraData = { msSinceCreated: Date.now() - createdAtMs };
    const reason = getWebGLUnavailableReason(error);
    if (reason === 'stopped-working') {
        // Context losses on this page before WebGL2 became unavailable: the evidence for
        // or against Chromium's crash limit (GPU acceleration off after 3 GPU crashes).
        const { contextLossCount } = getMapErrorCounts();
        logMapError({
            source: 'webgl',
            event: 'stopped_working',
            surface,
            error: sdkError,
            extraData: { ...extraData, contextLossCount },
        });
    } else if (reason === 'not-supported') {
        logMapError({ source: 'webgl', event: 'not_supported', surface, error: sdkError, extraData });
    } else {
        logMapError({ source: 'sdk', event: 'view_init_error', surface, error: sdkError, extraData });
    }
}
